import { apiService } from './api';
import { Project, PaginatedResponse, ApiResponse, CreateProjectData, UpdateProjectData } from '../types';

export interface ProjectFilters {
  search?: string;
  clientId?: string;
  page?: number;
  limit?: number;
  hasCompletedCheckout?: boolean;
}

export interface ProjectHistoricalUsageRow {
  id: string;
  projectId: string;
  clientId: string;
  rawSiteInput: string;
  normalizedDomain: string;
  matchedSiteId?: string | null;
  usedMonth: number;
  usedYear: number;
  note?: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  matchedSite?: {
    id: string;
    site_url: string;
    dr?: number;
    ahrefs_traffic?: number;
    category?: string;
  } | null;
}

class ProjectService {
  async getProjects(filters: ProjectFilters = {}): Promise<PaginatedResponse<Project>> {
    const response = await apiService.get<any>('/projects', filters);
    // Backend returns { success: true, data: [...], pagination: {...} }
    return {
      data: response.data || [],
      pagination: response.pagination || { page: 0, limit: 25, total: 0, pages: 0 }
    };
  }

  async getProject(id: string): Promise<Project> {
    const response = await apiService.get<ApiResponse<Project>>(`/projects/${id}`);
    return response.data;
  }

  async createProject(data: CreateProjectData): Promise<Project> {
    const response = await apiService.post<ApiResponse<Project>>('/projects', data);
    return response.data;
  }

  async updateProject(id: string, data: UpdateProjectData): Promise<Project> {
    const response = await apiService.put<ApiResponse<Project>>(`/projects/${id}`, data);
    return response.data;
  }

  async deleteProject(id: string): Promise<void> {
    await apiService.delete(`/projects/${id}`);
  }

  async getHistoricalUsage(projectId: string): Promise<ProjectHistoricalUsageRow[]> {
    const response = await apiService.get<{ success: boolean; data: ProjectHistoricalUsageRow[] }>(
      `/projects/${projectId}/historical-usage`
    );
    return response.data || [];
  }

  async uploadHistoricalUsage(
    projectId: string,
    file: File,
    usedMonth: number,
    usedYear: number,
    note?: string
  ): Promise<{ imported: number; matched: number; unmatched: number; skippedEmpty: number }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('usedMonth', String(usedMonth));
    formData.append('usedYear', String(usedYear));
    if (note?.trim()) formData.append('note', note.trim());

    const response = await apiService.upload<{ success: boolean; data: { imported: number; matched: number; unmatched: number; skippedEmpty: number } }>(
      `/projects/${projectId}/historical-usage/upload`,
      formData
    );
    return response.data;
  }

  async updateHistoricalUsageRow(
    projectId: string,
    rowId: string,
    data: Partial<Pick<ProjectHistoricalUsageRow, 'rawSiteInput' | 'usedMonth' | 'usedYear' | 'note' | 'isActive'>>
  ): Promise<ProjectHistoricalUsageRow> {
    const response = await apiService.put<{ success: boolean; data: ProjectHistoricalUsageRow }>(
      `/projects/${projectId}/historical-usage/${rowId}`,
      data
    );
    return response.data;
  }

  async deleteHistoricalUsageRow(projectId: string, rowId: string): Promise<void> {
    await apiService.delete(`/projects/${projectId}/historical-usage/${rowId}`);
  }
}

export const projectService = new ProjectService();
